import { Component, Inject, OnInit, OnDestroy, inject, effect, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TelnyxWebRTCService } from '../../services/telnyx-webrtc.service';
import { CallFacadeService } from '../../services/call';
import { CallLifecycleService } from '../../services/call/call-lifecycle.service';
import { LeadLookupService, LeadContactInfo, IncomingCallContext } from '../../services/lead-lookup.service';
import { CallAudioService } from '../../services/call-audio.service';
import { RingtoneConfigService } from '../../services/ringtone-config.service';
import { Subject, takeUntil, interval, combineLatest } from 'rxjs';
import { CallSession } from '../../../types/telnyx-webrtc';

export interface EnhancedIncomingCallData {
  phoneNumber: string;
  callerName?: string;
  leadId?: number;
  callSession: CallSession;
  fromAgent?: boolean;
  dbCallId?: number; // Database call ID from socket notification
  leadInfo?: {
    id: number;
    name?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    mobilePhone?: string;
    email?: string;
    status?: string;
    campusId?: number;
    campusName?: string;
    programId?: number;
    programName?: string;
    ownerId?: number;
    ownerName?: string;
    ownerEmail?: string;
  };
}

@Component({
  selector: 'app-enhanced-incoming-call',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './enhanced-incoming-call.component.html',
  styleUrls: ['./enhanced-incoming-call.component.scss']
})
export class EnhancedIncomingCallComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Service dependencies
  telnyxService = inject(TelnyxWebRTCService);
  calls = inject(CallFacadeService);
  private lifecycle = inject(CallLifecycleService);
  private leadLookupService = inject(LeadLookupService);
  private audioService = inject(CallAudioService);
  private ringtoneConfig = inject(RingtoneConfigService);
  
  // Component state signals
  isProcessing = signal(false);
  ringDuration = signal(0);
  isRinging = signal(true);
  showCallEnded = signal(false);
  
  // Lead and call context signals
  leadInfo = signal<LeadContactInfo | null>(null);
  callContext = signal<IncomingCallContext | null>(null);
  isKnownCaller = signal(false);
  isLoadingLeadInfo = signal(false);
  
  // Call answered state (used to determine if call was picked up before modal closes)
  isCallAnswered = signal(false);

  // Timer references for cleanup
  private autoDismissTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EnhancedIncomingCallData,
    private dialogRef: MatDialogRef<EnhancedIncomingCallComponent>
  ) {
    // Configure dialog behavior
    this.dialogRef.disableClose = true;

    // Auto-dismiss after 45 seconds if no action taken
    this.autoDismissTimer = setTimeout(() => {
      if (!this.isProcessing() && !this.showCallEnded() && !this.isCallAnswered()) {
        this.dismiss('timeout');
      }
    }, 45000);

    // Set up reactive effects
    this.setupReactiveEffects();
  }

  ngOnInit(): void {
    // Initialize call state
    this.isRinging.set(true);
    this.showCallEnded.set(false);
    this.isCallAnswered.set(false);

    this.startRingTimer();
    this.loadLeadInformation();
    this.startRingtone();
    this.setupCallStateSubscriptions();
  }



  ngOnDestroy(): void {
    // Clear auto-dismiss timer
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.stopRingtone();
  }

  /**
   * Answer the incoming call - simplified for webhook-based bridging
   */
  async answerCall(): Promise<void> {
    if (this.isProcessing()) return;

    try {
      this.isProcessing.set(true);
      this.isRinging.set(false);
      this.stopRingtone();

      // Check WebRTC connection
      const validationError = this.checkWebRTCConnection();
      if (validationError) {
        throw new Error(validationError);
      }

      // Simple WebRTC answer - backend will handle bridge automatically via webhooks
      const success = await this.calls.answerCall();

      if (!success) {
        this.showError('Failed to answer call - please try again');
        this.resetToRingingState();
      }
      // Note: Reactive effects will handle UI updates when bridge completes
    } catch (error: any) {
      console.error('[EnhancedIncomingCall] Answer call error:', error);
      this.handleError(error.message || 'Failed to answer call');
      this.resetToRingingState();
    }
  }

  /**
   * Decline the incoming call
   */
  async declineCall(): Promise<void> {
    if (this.isProcessing()) return;

    try {
      this.isProcessing.set(true);
      this.isRinging.set(false);
      this.stopRingtone();

      // End the call through the facade service
      await this.calls.endCall('cancelled', 'Call declined');
      
      this.showCallEnded.set(true);
      
      // Auto-dismiss after showing decline message
      setTimeout(() => {
        this.dismiss('declined');
      }, 2000);
    } catch (error: any) {
      this.handleError(error.message || 'Failed to decline call');
    } finally {
      this.isProcessing.set(false);
    }
  }

  /**
   * Dismiss the modal
   */
  dismiss(reason: string = 'dismissed'): void {
    this.stopRingtone();
    this.dialogRef.close({
      reason,
      leadInfo: this.leadInfo(),
      wasAnswered: this.isCallAnswered(),
      // Use database call ID from custom headers if available, otherwise fallback to WebRTC ID
      callId: this.data.dbCallId || this.data.callSession?.telnyxCallId || this.data.callSession?.id,
      phoneNumber: this.data.phoneNumber,
      duration: this.ringDuration() * 1000, // Ring duration in milliseconds (call duration tracked by GlobalCallPanel)
      leadId: this.data.leadId || this.leadInfo()?.id
    });
  }

  /**
   * Load lead information by phone number
   * Skips lookup if phone number is a WebRTC username (not a real phone number)
   */
  private loadLeadInformation(): void {
    // Check if leadInfo was already provided via SIP headers
    if (this.data.leadInfo?.id) {
      console.log('📞 [EnhancedIncomingCall] Using lead info from SIP headers:', this.data.leadInfo);

      // Convert leadInfo to LeadContactInfo format (includes campus, program, owner from SIP headers)
      const leadContactInfo: LeadContactInfo = {
        id: this.data.leadInfo.id,
        name: this.data.leadInfo.firstName || this.data.leadInfo.name || '',
        lastName: this.data.leadInfo.lastName || '',
        fullName: this.data.leadInfo.name ||
                  `${this.data.leadInfo.firstName || ''} ${this.data.leadInfo.lastName || ''}`.trim(),
        email: this.data.leadInfo.email,
        phoneNumber: this.data.leadInfo.phone || this.data.leadInfo.mobilePhone || this.data.phoneNumber || '',
        company_id: 0,
        status: this.data.leadInfo.status || 'active',
        // Campus, program, owner from SIP headers
        campus: this.data.leadInfo.campusId ? {
          id: this.data.leadInfo.campusId,
          name: this.data.leadInfo.campusName || ''
        } : undefined,
        program: this.data.leadInfo.programId ? {
          id: this.data.leadInfo.programId,
          name: this.data.leadInfo.programName || ''
        } : undefined,
        owner: this.data.leadInfo.ownerId ? {
          id: this.data.leadInfo.ownerId,
          name: this.data.leadInfo.ownerName?.split(' ')[0] || '',
          lastName: this.data.leadInfo.ownerName?.split(' ').slice(1).join(' ') || '',
          email: this.data.leadInfo.ownerEmail || ''
        } : undefined,
      };

      // Set lead info and known caller state
      this.leadInfo.set(leadContactInfo);
      this.isKnownCaller.set(true);
      this.isLoadingLeadInfo.set(false);

      // Set basic call context
      this.callContext.set({
        isKnownCaller: true,
        callHistory: { totalCalls: 0, averageDuration: 0 },
        suggestedActions: ['Review lead details', 'Update lead status']
      });

      return; // Skip API call
    }

    const phoneNumber = this.data.phoneNumber;

    // Skip lookup if phone number is invalid or WebRTC username
    if (!phoneNumber || phoneNumber.startsWith('webrtc') || phoneNumber === 'Unknown') {
      console.log('📞 [EnhancedIncomingCall] Skipping lead lookup - invalid phone:', phoneNumber);
      // Set default context - will be updated when socket provides real phone
      this.callContext.set({
        isKnownCaller: false,
        callHistory: { totalCalls: 0, averageDuration: 0 },
        suggestedActions: ['Waiting for caller info...']
      });
      return;
    }

    console.log('📞 [EnhancedIncomingCall] Looking up lead by phone:', phoneNumber);
    // this.performLeadLookup(phoneNumber);
  }

  /**
   * Perform the actual lead lookup
   */
  private performLeadLookup(phoneNumber: string): void {
    // Set loading state
    this.isLoadingLeadInfo.set(true);

    this.leadLookupService.getIncomingCallContext(phoneNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (context) => {
          this.callContext.set(context);
          this.isKnownCaller.set(context.isKnownCaller);
          this.isLoadingLeadInfo.set(false);

          if (context.lead) {
            this.leadInfo.set(context.lead);

            // Load call history
            this.leadLookupService.loadLeadCallHistory(context.lead.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  // Call history loaded
                },
                error: () => {
                  // Failed to load call history - continue without it
                }
              });
          }
        },
        error: () => {
          // Continue without lead info - show unknown caller
          this.isLoadingLeadInfo.set(false);
          this.callContext.set({
            isKnownCaller: false,
            callHistory: { totalCalls: 0, averageDuration: 0 },
            suggestedActions: ['Create new lead', 'Take detailed notes']
          });
        }
      });
  }

  /**
   * Called when phone number is updated (e.g., from socket notification)
   * Triggers lead lookup with the real phone number
   */
  onPhoneNumberUpdated(newPhoneNumber: string): void {
    if (newPhoneNumber && !newPhoneNumber.startsWith('webrtc') && newPhoneNumber !== 'Unknown') {
      console.log('📞 [EnhancedIncomingCall] Phone number updated, triggering lookup:', newPhoneNumber);
      this.performLeadLookup(newPhoneNumber);
    }
  }

  /**
   * Start ringtone with proper error handling
   */
  private async startRingtone(): Promise<void> {
    const config = this.ringtoneConfig.getConfig();

    if (!config.enabled) {
      this.showBrowserNotification();
      return;
    }

    try {
      const success = await this.audioService.startRingtone({
        audioFile: config.customUrl || '/assets/audio/ringtone.wav',
        volume: config.volume,
        vibrate: true
      });

      if (!success) {
        this.showBrowserNotification();
      }
    } catch (error) {
      this.showBrowserNotification();
    }
  }

  /**
   * Stop ringtone
   */
  private stopRingtone(): void {
    this.audioService.stopRingtone();
  }

  /**
   * Show browser notification as fallback
   */
  private showBrowserNotification(): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      const callerInfo = this.callerDisplayName();
      const lead = this.leadInfo();
      
      const notification = new Notification('Incoming Call', {
        body: `Call from ${callerInfo}${lead ? ` (${lead.program?.name || 'No program'})` : ''}`,
        icon: '/assets/icons/icon-192x192.png',
        tag: 'incoming-call',
        requireInteraction: true
      });

      // Close notification when component is destroyed
      this.destroy$.subscribe(() => {
        notification.close();
      });
    }
  }

  /**
   * Start ring duration timer
   */
  private startRingTimer(): void {
    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isRinging()) {
          this.ringDuration.update(duration => duration + 1);
        }
      });
  }

  /**
   * Set up reactive effects
   */
  private setupReactiveEffects(): void {
    // Monitor call state changes
    effect(() => {
      const state = this.calls.currentState();

      // Handle different call states based on new webhook flow
      switch (state) {
        case 'connecting':
          this.isRinging.set(false);
          this.stopRingtone();
          break;

        case 'connected':
          // Only transition if we're processing (user clicked answer) or still ringing
          // This prevents stale 'connected' states from previous calls auto-answering the modal
          if (!this.isCallAnswered() && (this.isProcessing() || this.isRinging())) {
            this.isCallAnswered.set(true);
            this.isRinging.set(false);
            this.stopRingtone();
            this.isProcessing.set(false);

            // Close modal immediately and let GlobalCallPanelComponent take over
            // The panel will show the call controls, duration, etc.
            console.log('📞 [EnhancedIncomingCall] Call connected - closing modal, GlobalCallPanel will take over');
            this.dismiss('answered');
            return; // Exit early since we're closing
          }
          this.isProcessing.set(false);
          break;

        case 'failed':
          this.isRinging.set(false);
          this.stopRingtone();
          this.showCallEnded.set(true);
          this.isProcessing.set(false);

          setTimeout(() => {
            this.dismiss(state);
          }, 3000);
          break;

        case 'ended':
          this.isRinging.set(false);
          this.stopRingtone();
          this.showCallEnded.set(true);
          this.isProcessing.set(false);

          setTimeout(() => {
            this.dismiss(state);
          }, 2000);
          break;

        case 'idle':
          if (this.isCallAnswered()) {
            this.isRinging.set(false);
            this.stopRingtone();
            this.showCallEnded.set(true);
            this.isProcessing.set(false);

            setTimeout(() => {
              this.dismiss(state);
            }, 2000);
          }
          break;

        default:
          break;
      }
    }, { allowSignalWrites: true });
  }

  /**
   * Set up call state subscriptions
   */
  private setupCallStateSubscriptions(): void {
    // Monitor call events
    this.telnyxService.getCallEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        // Handle all call ending events (call-ended, call-hangup, call-cancelled, call-failed)
        if (['call-ended', 'call-hangup', 'call-cancelled', 'call-failed'].includes(event.event)) {
          this.showCallEnded.set(true);
          this.isRinging.set(false);
          this.stopRingtone();

          setTimeout(() => {
            this.dismiss('ended');
          }, 2000);
        }
      });
  }


  /**
   * Check WebRTC connection for call answering
   */
  private checkWebRTCConnection(): string | null {
    try {
      // Check WebRTC connection status
      const webrtcInfo = this.telnyxService.getWebRTCConnectionInfo();
      
      if (!webrtcInfo.telnyxConnected) {
        return 'WebRTC connection not established. Please refresh the page and try again.';
      }
      
      return null; // Connection is ready
    } catch (error: any) {
      return `WebRTC connection error: ${error.message}`;
    }
  }

  /**
   * Handle error messages
   */
  private handleError(message: string): void {
    console.error('[EnhancedIncomingCall] Error:', message);
    // Simple error display - could be enhanced with toast/snackbar
  }

  private showError(message: string): void {
    this.handleError(message);
  }

  // Computed properties for performance optimization
  callerDisplayName = computed(() => {
    const lead = this.leadInfo();
    const isLoading = this.isLoadingLeadInfo();

    if (isLoading) {
      return 'Looking up caller...';
    }

    if (lead?.fullName) {
      return lead.fullName;
    } else if (lead?.name && lead?.lastName) {
      return `${lead.name} ${lead.lastName}`;
    } else if (this.data.callerName) {
      return this.data.callerName;
    }
    return 'Unknown Caller';
  });

  callerPhoneNumber = computed(() => {
    return this.data.phoneNumber || 'Unknown Number';
  });

  formattedRingDuration = computed(() => {
    const duration = this.ringDuration();
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  });

  callStateIcon = computed(() => {
    if (this.showCallEnded()) return 'call_end';
    if (this.isRinging()) return 'call';
    if (this.isCallAnswered()) return 'phone_in_talk';
    if (this.isProcessing()) return 'hourglass_empty';
    return 'call';
  });

  callStateMessage = computed(() => {
    if (this.showCallEnded()) return 'Call ended';

    const currentState = this.calls.currentState();

    if (currentState === 'connected') return 'Connected';
    if (currentState === 'connecting') return 'Connecting...';
    if (currentState === 'ringing' && this.isProcessing()) return 'Answering...';
    if (currentState === 'ringing') return 'Incoming call...';
    if (this.isProcessing()) return 'Processing...';
    
    return 'Call notification';
  });

  getCampusInfo(): string {
    const lead = this.leadInfo();
    return lead?.campus?.name || 'No campus assigned';
  }

  getProgramInfo(): string {
    const lead = this.leadInfo();
    return lead?.program?.name || 'No program assigned';
  }

  getOwnerInfo(): string {
    const lead = this.leadInfo();
    if (lead?.owner) {
      return `${lead.owner.name} ${lead.owner.lastName}`;
    }
    return 'No owner assigned';
  }

  getLeadStatus(): string {
    const lead = this.leadInfo();
    return lead?.status || 'Unknown';
  }

  getCallHistoryInfo(): string {
    const context = this.callContext();
    if (context && context.callHistory.totalCalls > 0) {
      return `${context.callHistory.totalCalls} previous calls`;
    }
    return 'First-time caller';
  }

  getSuggestedActions(): string[] {
    const context = this.callContext();
    return context?.suggestedActions || [];
  }


  getLeadInitials(): string {
    const lead = this.leadInfo();
    const isLoading = this.isLoadingLeadInfo();

    if (isLoading) {
      return '...';
    }

    if (lead?.name && lead?.lastName) {
      return `${lead.name[0]}${lead.lastName[0]}`.toUpperCase();
    } else if (lead?.fullName) {
      const names = lead.fullName.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    return '?';
  }

  // Removed duplicate getCallStateIcon - now using computed signal

  // Removed duplicate getCallStateMessage - now using computed signal

  // State check methods
  canAnswerCall(): boolean {
    // Modal is opened with valid call data, so if we're ringing and not processing, we can answer
    // No need to check activeCall() - that creates a race condition where button is disabled
    // until global state synchronization completes
    return this.isRinging() && !this.isProcessing() && !this.showCallEnded();
  }

  canDeclineCall(): boolean {
    return this.isRinging() && !this.isProcessing() && !this.showCallEnded();
  }

  private resetToRingingState(): void {
    this.isRinging.set(true);
    this.isCallAnswered.set(false);

    // Restart ringtone
    this.startRingtone().catch(() => {
      // Silently fail if ringtone can't restart
    });
  }
}